import { useSelector } from 'react-redux'
import { Link, useRouteMatch } from 'react-router-dom'
import Flex from 'styled-flex-component'

import ActionIcon from '@components/UI/ActionIcon'
import ConditionalLink from '@components/UI/ConditionalLink/ConditionalLink'
import { ConnectedTrackedRoute } from '@components/UI/TrackedRoute'
import { useIntl } from '@context/Internationalization/useIntl'
import useSubmissionListFilter from '@context/SubmissionListFilter/useSubmissionListFilter'
import { EventTrackingNames, InboxViewTypes } from '@enums'
import hooks from '@hooks'
import { IApplicationState } from '@store'
import {
  selectDisplayInfluencerView,
  selectIsPriceHidden,
  selectIsWeb3Campaign,
} from '@store/campaigns'
import { selectCampaignId } from '@store/router'
import Theme from '@theme'
import { Container } from '@tribegroup'
import { getMessage } from '@utils/message'

export const InboxViewOptions: React.FC = () => {
  const match = useRouteMatch()
  const { intl } = useIntl()
  const isInfluencerView = hooks.useIsInfluencerView()
  const { filter } = useSubmissionListFilter()
  const { isWeb3Campaign, campaignId, displayInfluencerView, isPriceHidden } = useSelector(
    (state: IApplicationState) => {
      const campaignId = selectCampaignId(match) as number

      return {
        isWeb3Campaign: selectIsWeb3Campaign(state, campaignId),
        campaignId,
        displayInfluencerView: selectDisplayInfluencerView(state, campaignId),
        isPriceHidden: selectIsPriceHidden(state, campaignId),
      }
    },
  )

  if (isWeb3Campaign) {
    return null
  }

  return (
    <Flex data-testid="inbox-view-options">
      <Container>
        <ConnectedTrackedRoute
          disableTracking={!isInfluencerView}
          eventName={EventTrackingNames.SwitchToTileView}
          campaignId={campaignId}
          additionalProperties={{ current_filter: filter }}
        >
          <ConditionalLink
            showLink={displayInfluencerView}
            to={{ search: '' }}
            data-testid="tile-view-link"
          >
            <ActionIcon
              glyph="view-by-tile"
              size={1.5}
              highlighted={!isInfluencerView}
              noHover={!displayInfluencerView}
              tooltip={getMessage(intl, 'inbox.viewOptions.tile')}
              color={Theme.defaultColor}
              elementName="tile-view-action-button"
            />
          </ConditionalLink>
        </ConnectedTrackedRoute>
      </Container>
      {displayInfluencerView && (
        <Container leftOuterSpacing={0.5}>
          <ConnectedTrackedRoute
            disableTracking={isInfluencerView}
            eventName={EventTrackingNames.SwitchToListView}
            campaignId={campaignId}
            additionalProperties={{ current_filter: filter }}
          >
            <Link
              to={{
                search: `viewType=${InboxViewTypes.Influencer}${
                  isPriceHidden ? '&sort_by=avg_engagement_pct' : ''
                }`,
              }}
              data-testid="influencer-view-link"
            >
              <ActionIcon
                glyph="view-by-creator"
                size={1.5}
                highlighted={isInfluencerView}
                tooltip={getMessage(intl, 'inbox.viewOptions.influencer')}
                color={Theme.defaultColor}
                elementName="influencer-view-action-button"
              />
            </Link>
          </ConnectedTrackedRoute>
        </Container>
      )}
    </Flex>
  )
}

export default InboxViewOptions
